<section
  class="queue-stats"
  [class.compact]="compact"
  [class.observatory]="appearance === 'observatory'"
  [class.dashboard]="appearance === 'dashboard'"
  [class.dashboard-preload]="appearance === 'dashboard' && !compact && !stats && !showInitialLoadingState && !errorMessage"
>
  @if (appearance === 'dashboard' && !compact) {
  <div
    class="dashboard-shell observatory-pattern-surface observatory-pattern-subtle"
    [class.observatory-pattern-off]="!usePatternedDashboardShell"
  >
    <div class="dashboard-header">
      <div class="dashboard-heading">
        <div class="dashboard-status-row">
          <h2 [class.dashboard-title-placeholder]="showInitialLoadingState && !stats">
            {{ title }}
          </h2>
          @if (stats) {
          <span
            class="health-pill"
            [class.state-healthy]="healthTone(stats) === 'healthy'"
            [class.state-active]="healthTone(stats) === 'active'"
            [class.state-warning]="healthTone(stats) === 'warning'"
            [class.state-backlogged]="healthTone(stats) === 'backlogged'"
            [class.state-down]="healthTone(stats) === 'down'"
          >
            {{ stats ? healthBadgeLabel(stats) : 'Loading' }}
          </span>
          }
        </div>
        <p
          class="dashboard-subtitle"
          [class.dashboard-subtitle-placeholder]="showInitialLoadingState && !stats"
        >
          {{
            stats
              ? healthDescription(stats)
              : ' '
          }}
        </p>
      </div>

      <div class="dashboard-meta-line">
        <span class="meta-chip">Checked {{ stats ? formatCheckedAtCompact(stats.checked_at_epoch_ms) : '—' }}</span>
        @if (isPaused) {
        <span class="meta-chip meta-chip-warning">Polling paused</span>
        }
      </div>
    </div>

    @if (errorMessage && !stats) {
    <div class="dashboard-inline-state">
      <p class="state-text error-text">{{ errorMessage }}</p>
    </div>
    }
    <div class="dashboard-primary-band">
      <article class="summary-tile">
        <span>Workers</span>
        <strong [class.summary-value-placeholder]="showInitialLoadingState && !stats">{{
          stats?.workers?.operational_count ?? '—'
        }}</strong>
        <p>Operational now</p>
      </article>

      <article class="summary-tile">
        <span>Processing</span>
        <strong [class.summary-value-placeholder]="showInitialLoadingState && !stats">{{
          stats?.jobs?.processing_count ?? '—'
        }}</strong>
        <p>Jobs in flight</p>
      </article>

      <article class="summary-tile" [class.summary-tile-flag]="(stats?.jobs?.queued_count ?? 0) > 0">
        <span>Queued</span>
        <strong [class.summary-value-placeholder]="showInitialLoadingState && !stats">{{
          stats?.jobs?.queued_count ?? '—'
        }}</strong>
        <p>Waiting to start</p>
      </article>

      <article
        class="summary-tile"
        [class.summary-tile-flag]="(stats?.jobs?.oldest_queued_age_seconds ?? 0) >= 60"
      >
        <span>Oldest queued</span>
        <strong [class.summary-value-placeholder]="showInitialLoadingState && !stats">
          {{ formatSeconds(stats?.jobs?.oldest_queued_age_seconds) }}
        </strong>
        <p>Queue delay</p>
      </article>

      <article class="summary-tile" [class.summary-tile-danger]="stats ? !stats.redis.ok : false">
        <span>Redis</span>
        <strong [class.summary-value-placeholder]="showInitialLoadingState && !stats">
          {{ stats ? (stats.redis.ok ? 'OK' : 'DOWN') : '—' }}
        </strong>
        <p>{{ stats ? stats.redis.ping_ms + ' ms ping' : 'Health check' }}</p>
      </article>

      <article class="summary-tile" [class.summary-tile-flag]="(stats?.warnings?.length ?? 0) > 0">
        <span>Warnings</span>
        <strong [class.summary-value-placeholder]="showInitialLoadingState && !stats">{{
          stats?.warnings?.length ?? '—'
        }}</strong>
        <p>{{ (stats?.warnings?.length ?? 0) > 0 ? 'Needs review' : 'None reported' }}</p>
      </article>
    </div>

    @if (stats?.query_limits) {
    <section class="query-limits-band" aria-label="Query limits">
      <div>
        <span>Max runtime</span>
        <strong>{{ formatSeconds(stats?.query_limits?.max_time_seconds) }}</strong>
      </div>
      <div>
        <span>Max rows</span>
        <strong>{{ formatNumber(stats?.query_limits?.max_result_rows) }}</strong>
      </div>
      <div>
        <span>Batch size</span>
        <strong>{{ formatNumber(stats?.query_limits?.batch_size) }}</strong>
      </div>
    </section>
    }

    @if ((stats?.warnings?.length ?? 0) > 0) {
    <section class="warning-banner">
      <div class="warning-banner-header">
        <h3>Operational warnings</h3>
        <span>{{ stats?.warnings?.length }}</span>
      </div>
      <ul>
        <li *ngFor="let warning of stats?.warnings || []">{{ warning }}</li>
      </ul>
    </section>
    }

    @if (showDetails) {
    <section class="workers-live-section">
      @if (showInitialLoadingState && isLoading && !stats) {
      <div class="workers-placeholder" aria-hidden="true">
        <div class="workers-placeholder-row"></div>
        <div class="workers-placeholder-row"></div>
        <div class="workers-placeholder-row"></div>
      </div>
      } @else if (errorMessage && !stats) {
      <p class="state-text error-text">{{ errorMessage }}</p>
      } @else if (!stats?.workers?.details?.length) {
      <p class="state-text subtle-text">No worker details available.</p>
      } @else {
      <div class="workers-table-wrap">
        <table aria-label="Workers">
          <thead>
            <tr>
              <th>Worker ID</th>
              <th>Status</th>
              <th>Current Job ID</th>
              <th>Last Heartbeat</th>
              <th>Uptime</th>
              <th>Current Job Age</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let worker of stats?.workers?.details || []; trackBy: trackWorker">
              <td>{{ displayWorkerId(worker.worker_id) }}</td>
              <td>
                <span class="worker-status-pill" [ngClass]="workerStatusClass(worker.status)">
                  {{ worker.status }}
                </span>
              </td>
              <td>{{ worker.current_job_id || '—' }}</td>
              <td>{{ formatSeconds(worker.heartbeat_age_seconds) }}</td>
              <td>{{ formatSeconds(worker.uptime_seconds) }}</td>
              <td>{{ formatSeconds(worker.current_job_age_seconds) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      }
    </section>
    }
  </div>
  } @else {
  <div class="header-row">
    <h2>{{ title }}</h2>
  </div>

  @if (isPaused) {
  <p class="state-text subtle-text">Polling paused while tab is hidden.</p>
  } @if (isLoading && !stats) {
  <p class="state-text">Loading queue stats...</p>
  } @if (errorMessage && !stats) {
  <p class="state-text error-text">{{ errorMessage }}</p>
  } @if (stats) { @if (compact) {
  <div class="compact-row-wrap">
    <div class="compact-metrics-row">
      <div class="compact-item">
        <span>Workers</span><strong>{{ stats.workers.operational_count }}</strong>
      </div>
      <div class="compact-item">
        <span>Processing</span><strong>{{ stats.jobs.processing_count }}</strong>
      </div>
      <div class="compact-item">
        <span>Queued</span><strong>{{ stats.jobs.queued_count }}</strong>
      </div>
      <div class="compact-item">
        <span>Oldest</span
        ><strong>{{ formatSeconds(stats.jobs.oldest_queued_age_seconds) }}</strong>
      </div>
      <div class="compact-item">
        <span>Redis</span>
        <strong [class.good]="stats.redis.ok" [class.bad]="!stats.redis.ok">
          {{ stats.redis.ok ? 'OK' : 'DOWN' }}
        </strong>
      </div>
      <div class="compact-item">
        <span>Warnings</span><strong>{{ stats.warnings.length }}</strong>
      </div>
    </div>
  </div>
  } @else {
  <div class="metrics-grid">
    <div class="metric-card">
      <div class="metric-label">Operational workers</div>
      <div class="metric-value">{{ stats.workers.operational_count }}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Processing now</div>
      <div class="metric-value">{{ stats.jobs.processing_count }}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Queued now</div>
      <div class="metric-value">{{ stats.jobs.queued_count }}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Oldest queued age</div>
      <div class="metric-value">{{ formatSeconds(stats.jobs.oldest_queued_age_seconds) }}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Redis health</div>
      <div class="metric-value" [class.good]="stats.redis.ok" [class.bad]="!stats.redis.ok">
        {{ stats.redis.ok ? 'OK' : 'DOWN' }}
      </div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Redis ping</div>
      <div class="metric-value">{{ stats.redis.ping_ms }} ms</div>
    </div>
  </div>

  <div class="checked-at">Last checked: {{ formatCheckedAt(stats.checked_at_epoch_ms) }}</div>

  @if (stats.warnings.length > 0) {
  <section class="warnings">
    <h3>Warnings</h3>
    <ul>
      <li *ngFor="let warning of stats.warnings">{{ warning }}</li>
    </ul>
  </section>
  } } @if (showDetails && !compact) {
  <section class="details">
    <h3>Queue Details</h3>
    <div class="details-grid">
      <div><strong>Queue name:</strong> {{ stats.queue_name }}</div>
      <div><strong>In system:</strong> {{ stats.jobs.in_system_count }}</div>
      <div><strong>Worker processing:</strong> {{ stats.workers.processing_count }}</div>
      <div><strong>Worker idle:</strong> {{ stats.workers.idle_count }}</div>
      <div>
        <strong>Configured processes/container:</strong>
        {{ stats.workers.configured_processes_per_container }}
      </div>
      <div>
        <strong>Heartbeat interval:</strong>
        {{ formatSeconds(stats.workers.heartbeat_interval_seconds) }}
      </div>
      <div>
        <strong>Heartbeat TTL:</strong> {{ formatSeconds(stats.workers.heartbeat_ttl_seconds) }}
      </div>
      <div><strong>Queue utilization:</strong> {{ stats.jobs.queue_utilization_pct }}%</div>
    </div>
  </section>

  <section class="workers">
    <h3>Worker Details</h3>
    @if (!stats.workers.details.length) {
    <p class="state-text subtle-text">No worker details available.</p>
    } @else {
    <div class="workers-table-wrap">
      <table>
        <thead>
          <tr>
            <th>Worker ID</th>
            <th>Hostname</th>
            <th>Process</th>
            <th>PID</th>
            <th>Status</th>
            <th>Current Job ID</th>
            <th>Heartbeat Age</th>
            <th>Uptime</th>
            <th>Current Job Age</th>
            <th>Last Seen</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let worker of stats.workers.details; trackBy: trackWorker">
            <td>{{ worker.worker_id }}</td>
            <td>{{ worker.hostname }}</td>
            <td>{{ worker.process_name }}</td>
            <td>{{ worker.pid }}</td>
            <td>{{ worker.status }}</td>
            <td>{{ worker.current_job_id || '—' }}</td>
            <td>{{ formatSeconds(worker.heartbeat_age_seconds) }}</td>
            <td>{{ formatSeconds(worker.uptime_seconds) }}</td>
            <td>{{ formatSeconds(worker.current_job_age_seconds) }}</td>
            <td>{{ formatCheckedAt(worker.last_seen_epoch_ms) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    }
  </section>
  } } @if (errorMessage && stats) {
  <p class="state-text error-text subtle-error">{{ errorMessage }}</p>
  }
  }
</section>
